import {Component, NgZone, ChangeDetectorRef, Input} from "@angular/core";

import {ModalConfig} from "../services/notification/modal.config";
import {EventBus} from "../event/EventBus";
import "rxjs/add/operator/map";
import "rxjs/add/operator/subscribeOn";

import {ComponentBase} from "../base/ComponentBase";
import {BugsnagLoggingService} from "../services/logging/bugsnag-logging.service";
import {PipelineService} from "../services/pipeline/pipeline.service";
import {ISelection, IText, ISentence} from  "../api/data-model";
import {PipelineSaveEvent, PIPELINE_API_STATUS} from "./PipelineSaveEvent";
import {Subscription} from "rxjs";
import {ContentCursor} from "../services/content/content.cursor";
import {HttpClient} from "@angular/common/http";

@Component({
    selector: "layout-editor",
    templateUrl: "./layout.editor.component.html",
    styleUrls: ["./pipeline.component.scss"]
})
export class LayoutEditorComponent extends ComponentBase {

    public activateModal: boolean = false;
    public status:string = "";
    public saveInProgress:boolean = false;
    public firstBlockSelected:boolean = false;
    public lastBlockSelected:boolean = false;
    public nextBlockIsImage:boolean = false;
    private textSubscription:Subscription;
    private sentenceSubscription:Subscription;
    public currentBlockIndex:number;
    public currentSentenceIndex:number;
    public currentTextBlock:IText;
    public currentSentence:ISentence;

    constructor(
        private http:HttpClient,
        private eventBus: EventBus,
        private modalConfig: ModalConfig,
        private logService: BugsnagLoggingService,
        private _ngZone: NgZone,
        private pipelineService:PipelineService,
        private contentCursor:ContentCursor,
        private ref: ChangeDetectorRef

    ) {
        super("LayoutEditorComponent");
    }

    get selection(): ISelection {
        return this.pipelineService.currentSourceSelection;
    }

    public makeActive():void {
        this.sentenceSubscription = this.contentCursor.sentenceChanged.subscribe(sentence => {
            this.currentSentenceIndex = sentence.parentText().sentences.indexOf(sentence);
            let blockIndex = sentence.parentText().parentSelection().text.indexOf(sentence.parentText());
            let block:IText = this.selection.text[blockIndex];
            this.currentSentence = block.sentences[this.currentSentenceIndex];
            this.logService.log("Sentence CLICKED: " + JSON.stringify(this.currentSentence));
            this.ref.detectChanges();
        });
        this.textSubscription = this.contentCursor.textChanged.subscribe(text => {
            this.currentBlockIndex = text.parentSelection().text.indexOf(text);
            this.currentTextBlock = this.selection.text[this.currentBlockIndex];
            if (this.currentTextBlock.mediaFormat) {
                this.contentCursor.selectWord(undefined);
            }
            this.firstBlockSelected = text.parentSelection().firstChild() === text;
            this.lastBlockSelected = !text.parentSelection().nextText(text);
            this.nextBlockIsImage = text.parentSelection().nextText(text) &&
                                    !!text.parentSelection().nextText(text).mediaFormat;
            this.logService.log("TEXT CLICKED: " + JSON.stringify(this.currentTextBlock));
            this.ref.detectChanges();
        });
    }

    public makeInactive():void {
        if (this.sentenceSubscription) {
            this.sentenceSubscription.unsubscribe();
        }
        if (this.textSubscription) {
            this.textSubscription.unsubscribe();
        }
    }

    public moveText(delta: number):void {
        // +1 moves down, -1 moves up
        let index = this.currentBlockIndex;
        let swapTextBlock = this.selection.text[index + delta];
        this.selection.text[index] = swapTextBlock;
        this.selection.text[index + delta] = this.currentTextBlock;
        this.currentBlockIndex = index + delta;
        this.showLayout();
    }

    public splitText():void {
        // make a copy of this.currentTextBlock -
        let newBlock:IText = JSON.parse(JSON.stringify(this.currentTextBlock));
        // delete sentences before currentSentenceIndex in new block
        newBlock.sentences.splice(0, this.currentSentenceIndex);
        // delete sentences staring with currentSentenceIndex from this.currentTextBlock
        let numFromEnd = this.currentTextBlock.sentences.length - this.currentSentenceIndex;
        this.currentTextBlock.sentences.splice(-numFromEnd, numFromEnd);
        // insert new block after this.currentTextBlock.
        this.selection.text.splice(this.currentBlockIndex + 1, 0, newBlock);
        this.currentTextBlock = newBlock;
        this.currentBlockIndex = this.selection.text.indexOf(newBlock);
        this.currentSentenceIndex = 0;
        this.currentSentence = this.currentTextBlock.sentences[0];

        this.showLayout();
    }

    public joinText():void {
        // concatenate sentences in next text block to this text block
        let nextTextBlock = (this.selection.text.length > this.currentBlockIndex + 1) ?
                             this.selection.text[this.currentBlockIndex + 1] : null;
        if (!nextTextBlock) {
            return;
        }
        this.currentTextBlock.sentences = this.currentTextBlock.sentences.concat(nextTextBlock.sentences);
        // delete nextTextBlock
        this.selection.text.splice(this.currentBlockIndex + 1, 1);
        this.currentSentenceIndex = 0;
        this.currentSentence = this.currentTextBlock.sentences[0];

        this.showLayout();
    }

    public addImageBefore():void {
        // make a copy of this.currentTextBlock -
        let newImage: IText = {mediaFormat: {source: "unknown", type: "img", height: "300", width: "300"}};
        // delete sentences before currentSentenceIndex in new block
        this.selection.text.splice(this.currentBlockIndex , 0, newImage);

        this.showLayout();
    }

    public addImageAfter():void {
        // make a copy of this.currentTextBlock -
        let newImage: IText = {mediaFormat: {source: "unknown", type: "img", height: "300", width: "300"}};
        // delete sentences before currentSentenceIndex in new block
        this.selection.text.splice(this.currentBlockIndex + 1 , 0, newImage);

        this.showLayout();
    }

    public deleteImage():void {

        // delete sentences before currentSentenceIndex in new block
        this.selection.text.splice(this.currentBlockIndex, 1);

        this.showLayout();
    }

    public showLayout():void {
        // TODO: should refactor this to pipelineService
        this.eventBus.next(new PipelineSaveEvent(PIPELINE_API_STATUS.start));
        this.eventBus.next(new PipelineSaveEvent(PIPELINE_API_STATUS.finish,
            this.selection));
        this.ref.detectChanges();
    }

    public saveFinalLayout(): void {
        this.saveInProgress = true;
        this.eventBus.next(new PipelineSaveEvent(PIPELINE_API_STATUS.start));
        this.pipelineService.saveSourceContent().subscribe(result => {
            this.logService.log("saved uninflated");
            this.eventBus.next(new PipelineSaveEvent(PIPELINE_API_STATUS.finish,
                this.pipelineService.currentSourceSelection));
            this.saveInProgress = false;
            this.ref.detectChanges();
        });
    }
}
